// Conexión con impresora térmica Bluetooth vía Web Bluetooth API.
// Soporta los 4 perfiles BLE más comunes en impresoras térmicas de bajo costo.
// Web Bluetooth solo funciona en Chrome para Android (no iOS Safari).

// Tipos mínimos de Web Bluetooth que no están en el lib DOM estándar
declare global {
  interface Navigator {
    bluetooth: {
      requestDevice(options: {
        acceptAllDevices?: boolean;
        filters?: Array<{ services?: string[] }>;
        optionalServices?: string[];
      }): Promise<BluetoothDevice>;
    };
  }
  interface BluetoothDevice {
    gatt?: BluetoothRemoteGATTServer;
    name?: string;
  }
  interface BluetoothRemoteGATTServer {
    connected: boolean;
    connect(): Promise<BluetoothRemoteGATTServer>;
    disconnect(): void;
    getPrimaryService(uuid: string): Promise<BluetoothRemoteGATTService>;
    getPrimaryServices(): Promise<BluetoothRemoteGATTService[]>;
  }
  interface BluetoothRemoteGATTService {
    getCharacteristic(uuid: string): Promise<BluetoothRemoteGATTCharacteristic>;
    getCharacteristics(): Promise<BluetoothRemoteGATTCharacteristic[]>;
  }
  interface BluetoothRemoteGATTCharacteristic {
    properties: { write: boolean; writeWithoutResponse: boolean };
    writeValue(value: BufferSource): Promise<void>;
    writeValueWithoutResponse(value: BufferSource): Promise<void>;
  }
}

// ── Perfiles BLE de impresoras térmicas ──────────────────────────────────────
// Intentamos cada par service/characteristic en orden hasta que uno funcione.
const BT_PROFILES = [
  // DETONGER P1S, WePrint / DeTong Electronic (ff00 family)
  { service: '0000ff00-0000-1000-8000-00805f9b34fb', char: '0000ff02-0000-1000-8000-00805f9b34fb' },
  { service: '0000ff00-0000-1000-8000-00805f9b34fb', char: '0000ff01-0000-1000-8000-00805f9b34fb' },
  // Zjiang, XP-58, KP-76B y la mayoría de impresoras chinas genéricas
  { service: '000018f0-0000-1000-8000-00805f9b34fb', char: '00002af1-0000-1000-8000-00805f9b34fb' },
  // Xprinter, MUNBYN, RONGTA y similares
  { service: 'e7810a71-73ae-499d-8c15-faa9aef0c3f2', char: 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f' },
  // ISSC Transparent UART (Peripage, Cat, etc.)
  { service: '49535343-fe7d-4ae5-8fa9-9fafd205e455', char: '49535343-8841-43f4-a8d4-ecbe34729bb3' },
  // Nordic UART Service — impresoras BLE modernas
  { service: '6e400001-b5a3-f393-e0a9-e50e24dcca9e', char: '6e400002-b5a3-f393-e0a9-e50e24dcca9e' },
];

// 512 bytes es el MTU máximo BLE; Chrome negocia y parte si hace falta.
const CHUNK = 512;
const DELAY = 5; // ms entre chunks (writeWithoutResponse no espera ACK)

// Estado del módulo (singleton por sesión del navegador)
let _device:   BluetoothDevice | null = null;
let _char:     BluetoothRemoteGATTCharacteristic | null = null;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ── API pública ───────────────────────────────────────────────────────────────

/** La API está disponible solo en Chrome para Android */
export function printerSupported(): boolean {
  return 'bluetooth' in navigator;
}

/** ¿Hay una conexión activa en este momento? */
export function printerConnected(): boolean {
  return !!_char && !!_device?.gatt?.connected;
}

/**
 * Abre el selector nativo de dispositivos Bluetooth, se conecta al elegido
 * y busca el primer characteristic escribible entre los perfiles conocidos.
 * Lanza Error con mensaje en español si algo falla.
 */
export async function connectPrinter(): Promise<void> {
  if (!printerSupported()) {
    throw new Error(
      'Web Bluetooth no está disponible. Abrí esta app en Chrome para Android.'
    );
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: [...new Set(BT_PROFILES.map((p) => p.service))],
  });

  _device = device;

  let server: BluetoothRemoteGATTServer;
  try {
    server = await device.gatt!.connect();
  } catch {
    throw new Error('No se pudo conectar a la impresora. Verificá que esté encendida y sin otro dispositivo conectado.');
  }

  // Intentar perfiles conocidos
  for (const { service, char } of BT_PROFILES) {
    try {
      const svc = await server.getPrimaryService(service);
      _char = await svc.getCharacteristic(char);
      return; // éxito
    } catch {
      // probar el siguiente
    }
  }

  // Fallback: buscar cualquier characteristic escribible
  try {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
      const chars = await svc.getCharacteristics();
      for (const c of chars) {
        if (c.properties.write || c.properties.writeWithoutResponse) {
          _char = c;
          return;
        }
      }
    }
  } catch { /* ignore */ }

  throw new Error(
    'La impresora no expone un servicio de escritura reconocido. ' +
    'Confirmá que sea una impresora térmica BLE compatible con ESC/POS.'
  );
}

/**
 * Envía los bytes ESC/POS a la impresora en chunks de 200 bytes.
 */
export async function printData(data: Uint8Array): Promise<void> {
  if (!_char) throw new Error('Impresora no conectada. Conectala primero.');
  if (!_device?.gatt?.connected) {
    _char = null;
    throw new Error('La impresora se desconectó. Volvé a conectarla.');
  }

  for (let i = 0; i < data.length; i += CHUNK) {
    const chunk = data.slice(i, i + CHUNK);
    if (_char.properties.writeWithoutResponse) {
      await _char.writeValueWithoutResponse(chunk);
    } else {
      await _char.writeValue(chunk);
    }
    if (i + CHUNK < data.length) await sleep(DELAY);
  }
}

/** Desconecta y limpia el estado */
export function disconnectPrinter(): void {
  try { _device?.gatt?.disconnect(); } catch { /* ignore */ }
  _device = null;
  _char   = null;
}
